/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type {
  TimeData,
  TranscriptOutputLineParsedTime,
  TranscriptOutputLineRawTime
} from '@repo/common';
import { LRUCache } from 'lru-cache';

export const sleep = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

// LRU cache for parseRawOutput results
// Cache key format: `${raw}|${index}|${parseTime}`
const parseRawOutputCache = new LRUCache<
  string,
  TranscriptOutputLineRawTime | TranscriptOutputLineParsedTime
>({
  max: 200, // Maximum number of entries to cache
  ttl: 1000 * 60 * 5 // 5 minutes TTL
});

/**
 * Clear the parseRawOutput cache
 */
export function clearParseRawOutputCache(): void {
  parseRawOutputCache.clear();
}

/**
 * Get cache statistics for parseRawOutput
 */
export function getParseRawOutputCacheStats() {
  return {
    size: parseRawOutputCache.size,
    max: parseRawOutputCache.max,
    ttl: parseRawOutputCache.ttl
  };
}

export function secondsToTimecode(seconds: number, doTruncate = false) {
  const HOUR = 3600;
  const MINUTE = 60;
  const h = Math.floor(seconds / HOUR);
  const m = Math.floor(seconds / MINUTE) % 60;
  const s = doTruncate ? Math.floor(seconds) % 60 : seconds % 60;

  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s
    .toString()
    .padStart(2, '0')}`;
}

export function timestampStrToSeconds(timestampStr: string): number {
  // Ensure input matches expected format
  const regex = /^\d{2}:\d{2}:\d{2}(\.\d{1,3})?$/; // Matches HH:MM:SS or HH:MM:SS.mmm
  if (!regex.test(timestampStr)) {
    throw new Error(`Invalid timestamp format: ${timestampStr}`);
  }
  // Split into components and parse
  const parts = timestampStr
    .split(':')
    .map((part, index) => (index === 2 ? parseFloat(part) : parseInt(part, 10)));
  if (parts.length !== 3) {
    throw new Error(`Unexpected timestamp format: ${timestampStr}`);
  }
  // Convert to seconds
  const [hours, minutes, seconds] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}

export function linesToUniqueMap(transcription: string[]): Map<string, string> {
  const myMap = new Map<string, string>();
  for (const line of transcription) {
    const { start } = parseStartEnd(line);
    const startTimeStr = start[0];
    myMap.set(startTimeStr, line);
  }
  return new Map<string, string>([...myMap].sort((a, b) => String(a[0]).localeCompare(b[0])));
}

export function timecodeToTimeData(matchArray: RegExpMatchArray): TimeData {
  if (!matchArray) {
    throw new Error('No match array');
  }
  const h = parseInt(matchArray[1]);
  const m = parseInt(matchArray[2]);

  const s = parseFloat(matchArray[3]);
  return {
    hours: h,
    minutes: m,
    seconds: s,
    hoursStr: h.toString().padStart(2, '0'),
    minutesStr: m.toString().padStart(2, '0'),
    secondsStr: s.toFixed(3).padStart(6, '0') // using toFixed to maintain 3 decimal places
  };
}

export function secondsToTimeData(seconds: number): TimeData {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return {
    hours: h,
    minutes: m,
    seconds: s,
    hoursStr: h.toString().padStart(2, '0'),
    minutesStr: m.toString().padStart(2, '0'),
    secondsStr: s.toFixed(3).padStart(6, '0')
  };
}

export function timeDataToSeconds(time: TimeData): number {
  return time.hours * 3600 + time.minutes * 60 + time.seconds;
}

// export function formatRawOutput(start: TimeData, end: TimeData, text: string) {
// 	return `[${start.hoursStr}:${start.minutesStr}:${start.secondsStr} --> ${end.hoursStr}:${end.minutesStr}:${end.secondsStr}] ${text}`;
// }

type TimeDataStr = Pick<TimeData, 'hoursStr' | 'minutesStr' | 'secondsStr'>;

export function formatVttOutput(
  start: TimeDataStr,
  end: TimeDataStr,
  text: string,
  speaker?: string
) {
  return formatVtt(formatTimeData(start), formatTimeData(end), text, speaker);
}

export function formatVtt(start: string, end: string, text: string, speaker?: string) {
  if (speaker !== undefined) {
    return `${start} --> ${end}- (${speaker}) ${text}`;
  }
  return `${start} --> ${end}- ${text}`;
}

export function formatTimeData(timeData: TimeDataStr): string {
  return `${timeData.hoursStr}:${timeData.minutesStr}:${timeData.secondsStr}`;
}

export function parsedToRawTime(
  input: TranscriptOutputLineParsedTime
): TranscriptOutputLineRawTime {
  return {
    ...input,
    start: formatTimeData(input.start),
    end: formatTimeData(input.end)
  };
}

// Multiple function overloads
export function parseRawOutput(
  raw: string,
  index: number,
  parseTime: false
): TranscriptOutputLineRawTime;
export function parseRawOutput(
  raw: string,
  index: number,
  parseTime: true
): TranscriptOutputLineParsedTime;

// Implement the function
export function parseRawOutput(
  raw: string,
  index: number,
  parseTime: boolean = true
): TranscriptOutputLineRawTime | TranscriptOutputLineParsedTime {
  // Create cache key from input parameters
  const cacheKey = `${raw}|${index}|${parseTime}`;

  // Check cache first
  const cached = parseRawOutputCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const timestampRegex = /(\d\d):(\d\d):(\d\d\.\d{3})/g;
  const timestamp = raw.substring(0, raw.indexOf(']'));
  const text = raw.substring(raw.indexOf(']') + 2);
  const match = [...timestamp.matchAll(timestampRegex)];
  try {
    const start = timecodeToTimeData(match[0]);
    const end = timecodeToTimeData(match[1]);
    const vtt = `
${formatVttOutput(start, end, text)}`;

    const baseOutput = {
      vtt,
      text,
      delta: index,
      startSeconds: timeDataToSeconds(start),
      endSeconds: timeDataToSeconds(end)
    };

    const parsed: TranscriptOutputLineParsedTime = {
      start,
      end,
      ...baseOutput
    };

    const result = parseTime ? parsed : parsedToRawTime(parsed);

    // Cache the result
    parseRawOutputCache.set(cacheKey, result);

    return result;
  } catch (err) {
    console.warn('Could not parse raw output', raw, err);
    throw err;
  }
}

export function parseStartEnd(raw: string) {
  const timestampRegex = /(\d\d:\d\d:\d\d\.\d{3})/g;
  const timestamp = raw.substring(0, raw.indexOf(']'));
  // const text = raw.substring(raw.indexOf(']') + 1);
  const match = [...timestamp.matchAll(timestampRegex)];
  return { start: match[0], end: match[1] };
}

export function reduceTranscript(transcript: string[], lookBack = 3) {
  let result: string[] = transcript;
  const numberPreserved = Math.max(transcript.length - lookBack, 0);
  if (!transcript.length) {
    return { result, numberPreserved };
  }

  const myMap = new Map<string, string>();

  for (const line of transcript) {
    const { start } = parseStartEnd(line);
    const startTimeStr = start[0];
    myMap.set(startTimeStr, line);
  }

  result = [...myMap].sort((a, b) => String(a[0]).localeCompare(b[0])).map((a) => a[1]);
  return { result, numberPreserved };
}

// const differenceInSeconds = (startTime: string, endTime: string): number => {
// 	const startParts = startTime.split(":").map((part) => parseFloat(part));
// 	const endParts = endTime.split(":").map((part) => parseFloat(part));
//
// 	if (startParts.length !== 3 || endParts.length !== 3) {
// 		return 0;
// 	}
//
// 	const startSeconds = startParts[0] * 3600 + startParts[1] * 60 + startParts[2];
// 	const endSeconds = endParts[0] * 3600 + endParts[1] * 60 + endParts[2];
//
// 	return parseFloat((endSeconds - startSeconds).toFixed(2));
// };

export function msToTimeCode(ms: number): string {
  const hours = String(Math.floor(ms / 3600000)).padStart(2, '0');
  const minutes = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
  const seconds = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
  const milliseconds = String(Math.round(ms % 1000)).padStart(3, '0'); // Round to the nearest millisecond

  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

export function convertToTranscriptionOutput(
  startTime: string,
  endTime: string,
  text: string
): string {
  return `[${startTime} - ${endTime}] ${text}`;
}

export function transcriptionToTimeCode(startOffsetMs: number, inputHundredths: string): string {
  // Use regular expression to capture groups: start time, end time, and text
  const regex = /\[(\d+) - (\d+)]:\s+(.+)/g;
  let match;
  let output = '';

  while ((match = regex.exec(inputHundredths)) !== null) {
    const startTime = msToTimeCode(startOffsetMs + 10 * Number(match[1]));
    const endTime = msToTimeCode(startOffsetMs + 10 * Number(match[2]));
    const text = match[3];

    output += `${convertToTranscriptionOutput(startTime, endTime, text)}\n`;
  }

  return output.trim();
}
